import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useCallback, useRef } from 'react';
import { integrationService, Integration, PixelHealthRow, UpdateIntegrationData } from '@/services/integrationService';
import { Pixel, PixelPlatform, AddPixelData, PixelHealth } from '@/types/pixel';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

/**
 * Chave do agregado de saúde por vídeo+plataforma.
 */
function healthKey(idVideo: string, platform: PixelPlatform): string {
  return `${idVideo}:${platform}`;
}

function healthFromRow(row: PixelHealthRow): PixelHealth {
  return {
    first_at: row.first_at,
    last_at: row.last_at,
    last_event: row.last_event,
    total: row.total ?? 0,
    last24h: row.last24h ?? 0,
  };
}

/**
 * Deriva status de saúde honesto (substitui o hardcoded 'pending'):
 * - sucesso: evento aceito nas últimas 24h
 * - erro: já disparou, mas está mudo há +24h (config provável OK, página morta
 *   ou setup quebrado — vale re-verificar a URL vinculada)
 * - pending: nenhum disparo registrado ainda
 */
function verificationFromHealth(health?: PixelHealth): 'success' | 'error' | 'pending' {
  if (!health || !health.total) return 'pending';
  if (health.last24h > 0) return 'success';
  return 'error';
}

/**
 * Resolve a plataforma pela empresa (fonte oficial).
 * Fallback: extractPlatformFromUrl (legado, só quando company ausente).
 */
function platformFromCompany(companyName?: string): PixelPlatform | null {
  if (!companyName) return null;
  const n = companyName.toLowerCase();
  if (n.includes('google')) return 'google-ads';
  if (n.includes('tiktok')) return 'tiktok';
  if (n.includes('meta') || n.includes('facebook')) return 'facebook';
  return null;
}

/**
 * Extrai a plataforma do pixel a partir do endpoint_url (LEGADO).
 */
function extractPlatformFromUrl(url: string): PixelPlatform {
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('facebook') || lowerUrl.includes('meta')) {
    return 'facebook';
  }
  if (lowerUrl.includes('google') || lowerUrl.includes('ads')) {
    return 'google-ads';
  }
  if (lowerUrl.includes('tiktok')) {
    return 'tiktok';
  }

  // Padrão: facebook
  return 'facebook';
}

/**
 * Transforma dados da API para o formato do frontend.
 * Plataforma: company.name primeiro; URL só como fallback legado.
 * pixelId: id_plan (novo) com fallback para id (legado).
 */
function transformIntegrationToPixel(
  integration: Integration,
  videoName?: string,
  health?: PixelHealth,
): Pixel {
  const platform = platformFromCompany(integration.company?.name) ?? extractPlatformFromUrl(integration.endpoint_url);
  return {
    id: integration.id,
    pixelId: integration.id_plan || integration.id,
    platform,
    token: integration.api_key || integration.endpoint_url,
    siteUrl: integration.endpoint_url,
    videoId: integration.id_video || '',
    flowId: integration.id_flow || undefined,
    coveredVideoIds: integration.covered_video_ids,
    videoName: videoName || 'Vídeo',
    status: integration.status === 'active' ? 'ativo' : 'desativado',
    active: integration.status === 'active',
    createdAt: integration.created_at || integration.createdAt,
    lastEventAt: health?.last_at,
    verificationStatus: verificationFromHealth(health),
    health,
    eventsSent: integration.event_source || undefined,
    // Campos da API
    endpoint_url: integration.endpoint_url,
    id_company: integration.id_company,
    company: integration.company,
  };
}

/**
 * Hook para gerenciar pixels (integrações de tipo 'pixel')
 */
export function usePixels(flowId?: string, flowName?: string) {
  useLanguage();
  const { user } = useAuth();
  const scope = `${user?.id_user || ''}:${flowId || 'videos'}`;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const requestId = useRef(0);
  const [state, setState] = useState<{ scope: string; pixels: Pixel[]; loading: boolean; error: string | null }>({ scope, pixels: [], loading: true, error: null });
  const videoNamesCacheRef = useRef<Record<string, string>>({});
  const transform = useCallback((row: Integration, health?: PixelHealth) =>
    transformIntegrationToPixel(row, row.id_flow ? flowName || 'Funil da VSL Dinâmica' : videoNamesCacheRef.current[row.id_video] || 'Vídeo', row.id_flow ? row.health : health), [flowName]);

  const fetchPixels = useCallback(async () => {
    const id = ++requestId.current;
    const isCurrent = () => currentScope.current === scope && requestId.current === id;
    if (!user?.id_user) {
      setState({ scope, pixels: [], loading: false, error: null });
      return;
    }
    setState((previous) => ({ scope, pixels: previous.scope === scope ? previous.pixels : [], loading: true, error: null }));
    try {
      let pixels: Pixel[];
      if (flowId) {
        pixels = (await integrationService.getFlowPixels(flowId)).map((row) => transform(row));
      } else {
        const [rows, healthRows] = await Promise.all([
          integrationService.getByUserIdAndType(user.id_user, 'pixel', false),
          integrationService.getPixelHealth(user.id_user),
        ]);
        const healthMap = new Map(healthRows.map((row) => [healthKey(row.id_video, row.platform as PixelPlatform), healthFromRow(row)]));
        pixels = rows.filter((row) => !row.id_flow).map((row) => {
          const platform = platformFromCompany(row.company?.name) ?? extractPlatformFromUrl(row.endpoint_url);
          return transform(row, healthMap.get(healthKey(row.id_video, platform)));
        });
      }
      if (isCurrent()) setState({ scope, pixels, loading: false, error: null });
    } catch (error: unknown) {
      const err = error as { message?: string; response?: { status?: number } };
      if (isCurrent()) setState({ scope, pixels: [], loading: false, error: !flowId && err.response?.status === 404 ? null : err.message || 'Erro ao buscar pixels' });
    }
  }, [user?.id_user, flowId, scope, transform]);

  const addPixel = useCallback(async (data: AddPixelData) => {
    if (!user?.id_user) throw new Error(translateInterfaceText("Usuário não autenticado"));
    try {
      const body = { endpoint_url: data.siteUrl, api_key: data.token, id_plan: data.pixelId, platform: data.platform, status: data.active ? 'active' as const : 'inactive' as const };
      if (flowId) {
        if (data.flowId !== flowId) throw new Error(translateInterfaceText("O funil selecionado mudou. Abra o cadastro novamente."));
        await integrationService.createFlowPixel(flowId, body);
      } else {
        if (!data.videoId) throw new Error(translateInterfaceText("Selecione um vídeo."));
        await integrationService.create({ ...body, type: 'pixel', id_user: user.id_user, id_video: data.videoId });
        if (data.videoName) videoNamesCacheRef.current[data.videoId] = data.videoName;
      }
      if (currentScope.current === scope) await fetchPixels();
      toast.success(translateInterfaceText("Pixel adicionado com sucesso!"));
    } catch (error: unknown) {
      const err = error as { message?: string; response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || err.message || translateInterfaceText("Erro ao adicionar pixel"));
      throw error;
    }
  }, [user?.id_user, flowId, scope, fetchPixels]);

  const updatePixel = useCallback(async (pixelId: string, data: { active?: boolean; token?: string; siteUrl?: string }) => {
    const body: UpdateIntegrationData = {};
    if (data.active !== undefined) body.status = data.active ? 'active' : 'inactive';
    if (data.token && data.token !== '***') body.api_key = data.token;
    if (data.siteUrl) body.endpoint_url = data.siteUrl;
    try {
      const updated = flowId ? await integrationService.updateFlowPixel(flowId, pixelId, body) : await integrationService.update(pixelId, body);
      const pixel = transform(updated);
      if (currentScope.current === scope) setState((prev) => ({ ...prev, pixels: prev.pixels.map((old) => old.id === pixelId ? { ...pixel, health: pixel.health || old.health, lastEventAt: pixel.lastEventAt || old.lastEventAt, verificationStatus: verificationFromHealth(pixel.health || old.health) } : old) }));
      toast.success(translateInterfaceText("Pixel atualizado com sucesso!"));
      return pixel;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : translateInterfaceText("Erro ao atualizar pixel"));
      throw error;
    }
  }, [flowId, scope, transform]);

  const deletePixel = useCallback(async (pixelId: string) => {
    try {
      if (flowId) await integrationService.deleteFlowPixel(flowId, pixelId);
      else await integrationService.delete(pixelId);
      if (currentScope.current === scope) setState((prev) => ({ ...prev, pixels: prev.pixels.filter((p) => p.id !== pixelId) }));
      toast.success(translateInterfaceText("Pixel excluído com sucesso!"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : translateInterfaceText("Erro ao deletar pixel"));
      throw error;
    }
  }, [flowId, scope]);

  const setVideoNamesCache = useCallback((names: Record<string, string>) => {
    videoNamesCacheRef.current = { ...videoNamesCacheRef.current, ...names };
  }, []);

  return {
    pixels: state.scope === scope ? state.pixels : [],
    isLoading: state.scope !== scope || state.loading,
    error: state.scope === scope ? state.error : null,
    fetchPixels, addPixel, updatePixel, deletePixel, setVideoNamesCache,
  };
}
